import { Journey } from '@audibene/ta.utils-opportunity'
import { useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { AppointmentType, AvailabilityMode, EventType } from '../../constants'
import { useEventTracking } from '../../hooks'
import { useAppointmentsService } from '../../hooks/services'
import { GetAvailabilitiesRequest } from '../../hooks/services/appointments/availabilities'
import { GetAvailabilitiesRequestEventData, Resource } from '../../types'
import { logFactory } from '../../util'
import { useAvailabilitiesRetry } from '../useAvailabilitiesRetry'
import { useAvailabilityCacheEnabled } from '../useAvailabilityCacheEnabled'
import { useFeatureFlags } from '../useFeatureFlags'

const hookName = 'useRecommendedAvailabilities'
const log = logFactory(hookName)

export interface RecommendedAvailabilitiesProps {
  appointmentType: AppointmentType
  dateEnd: Date
  dateStart: Date
  durationInMinutes: number
  isEnabled?: boolean
  journey: Journey
  prsRecommendationGid?: string
  resources?: Resource[]
  timeZone: string
  mode?: AvailabilityMode
}

const useRecommendedAvailabilities = ({
  appointmentType,
  dateEnd,
  dateStart,
  durationInMinutes,
  isEnabled = true,
  journey,
  prsRecommendationGid,
  resources,
  timeZone,
  mode,
}: RecommendedAvailabilitiesProps) => {
  const { getAvailabilities, isReady } = useAppointmentsService()
  const { track: trackEvent } = useEventTracking()
  const resourceGids = useMemo(() => resources?.map((resource) => resource.gid) ?? [], [resources])

  const shouldUseCache = useAvailabilityCacheEnabled()
  const [isRecommendationBasedAvailabilitiesEnabled] = useFeatureFlags(['RECOMMENDATION_BASED_AVAILABILITIES'])

  const useRecommendationGid = Boolean(isRecommendationBasedAvailabilitiesEnabled && prsRecommendationGid)

  const params: GetAvailabilitiesRequest = {
    allow_integration_errors: true, // Required because we could have resources without integration configured or in a wrong state
    appointment_type: appointmentType,
    customer_timezone: timeZone,
    duration_in_minutes: durationInMinutes,
    date_end: dateEnd,
    date_start: dateStart,
    journey,
    ...(useRecommendationGid ? { recommendation_gid: prsRecommendationGid } : { resource_gids: resourceGids }),
    mode,
    use_cache: shouldUseCache,
  }

  const fetchAvailabilities = async () => {
    log(`📆 Fetching resource availabilities...`, params)

    const timeStart = Date.now()
    const availabilities = await getAvailabilities(params)
    const timeEnd = Date.now()
    const timeInMs = timeEnd - timeStart

    if (availabilities.responseStatus === 200) {
      const eventData: GetAvailabilitiesRequestEventData = {
        requestParams: params,
        timeInMs,
      }

      trackEvent(EventType.APPOINTMENT_SERVICE_TIME_LOADING_AVAILABILITIES, { data: eventData })
      log(`📆 Found resource availabilities`, availabilities)
    }

    return availabilities
  }

  const isQueryEnabled = Boolean(isReady && isEnabled)

  const results = useQuery([hookName, isRecommendationBasedAvailabilitiesEnabled, prsRecommendationGid, params], fetchAvailabilities, {
    enabled: isQueryEnabled,
  })

  const { checkAvailabilitiesRequestRetry } = useAvailabilitiesRetry({ results })
  const { isLoading, data } = results

  useEffect(() => {
    checkAvailabilitiesRequestRetry()
  }, [results])

  return {
    ...results,
    data,
    isLoading,
  }
}

export { hookName as useRecommendedAvailabilitiesHookName, useRecommendedAvailabilities }
